import styled, { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
import { useContext } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import H4 from '@base/H4/H4';
import CustomModal from '@comp/CustomModal/CustomModal';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { Spacings, Fonts } from '@styles/index';
import H2 from '@base/H2/H2';
import imageHelper from '@utils/imageHelper';

const SectionWrapper = styled.section`
  display: grid;
  grid-template: ${({ $isFloating }) =>
    $isFloating
      ? 'max-content max-content / 1fr'
      : '1fr / max-content max-content'};
  grid-template-areas: ${({ $isFloating }) =>
    $isFloating
      ? "'image-wrapper' 'content-wrapper'"
      : "'content-wrapper image-wrapper'"};
  border-radius: ${Spacings.SPACING_2B};
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => boxShadowGenerator({ theme })};
  width: ${({ $isFloating }) => ($isFloating ? '440px' : '1136px')};
  margin: auto;
  ${({ $isFloating }) =>
    $isFloating ? '' : 'margin-top: 160px; justify-content: space-between;'}
  @media (max-width: 70.95em) {
    width: 728px;
    overflow: hidden;
    margin-top: 96px;
  }
  @media (max-width: 47.95em) {
    width: calc(100% - 32px);
    display: flex;
    flex-direction: column-reverse;
    margin: 96px 16px 8px;
    box-shadow: 0px 8px 16px rgba(221, 230, 237, 0.4);
  }
`;

const Label = styled(H2)`
  font-size: ${({ $isFloating }) => ($isFloating ? Fonts.H3 : '40px')};
  ${({ $isFloating }) => ($isFloating ? 'text-align: center;' : '')}
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  font-weight: ${Fonts.BOLD};
  @media (max-width: 70.95em) {
    font-size: 22px;
  }
  @media (max-width: 47.95em) {
    font-size: 28px;
  }
`;

const SecLabel = styled(H4)`
  margin: ${({ $isFloating }) =>
    $isFloating
      ? `${Spacings.SPACING_2B} ${Spacings.SPACING_10B} 0px`
      : `${Spacings.SPACING_6B} 0px 0px`};
  font-weight: ${Fonts.REGULAR};
  ${({ $isFloating }) => ($isFloating ? `font-size: ${Fonts.H5}` : '')}
  @media (max-width: 70.95em) {
    font-size: 16px;
    margin-top: 12px;
  }
  @media (max-width: 47.95em) {
    margin-top: 4px;
    line-height: 150%;
  }
`;

const TextWrapper = styled.div`
  padding: ${({ $isFloating }) =>
    $isFloating
      ? `${Spacings.SPACING_10B} 0 ${Spacings.SPACING_12B} 0`
      : `116px 0 ${Spacings.SPACING_20B} ${Spacings.SPACING_24B}`};
  width: ${({ $isFloating }) => ($isFloating ? '100%' : '465px')};
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 0 0 ${Spacings.SPACING_2B} ${Spacings.SPACING_2B};
  grid-area: content-wrapper;
  ${({ $isFloating }) => ($isFloating ? 'text-align: center' : '')}
  @media (max-width: 70.95em) {
    padding: 58px 62px 54px;
  }
  @media (max-width: 47.95em) {
    padding: 40px 16px;
    width: 100%;
    text-align: center;
  }
`;

const PhoneWrapper = styled.div`
  background-color: ${({ theme, $isFloating }) =>
    $isFloating ? theme.colors.whiteV4 : theme.colors.white};
  ${({ $isFloating }) =>
    $isFloating ? `padding-top: ${Spacings.SPACING_12B};` : ''}
  ${({ $isFloating }) => ($isFloating ? 'text-align: center;' : '')}
  border-radius: ${Spacings.SPACING_2B} ${Spacings.SPACING_2B} 0 0;
  grid-area: image-wrapper;
  @media (max-width: 70.95em) {
    display: flex;
    align-items: flex-end;
  }
  @media (max-width: 47.95em) {
    display: flex;
    justify-content: center;
    padding: 40px 41px 0;
    background: linear-gradient(180deg, #ffffff 73.44%, #f0f4f7 100%);
  }
`;

const AppContainer = styled.div`
  margin-top: ${({ $isFloating }) =>
    $isFloating ? Spacings.SPACING_7B : Spacings.SPACING_10B};
  ${({ $isFloating }) => ($isFloating ? 'text-align: center;' : '')}
  @media (max-width: 70.95em) {
    margin-top: 32px;
  }
  @media (max-width: 47.95em) {
    margin-top: 24px;
    display: flex;
    justify-content: center;
    grid-column-gap: 12px;
  }
`;

const Img = styled(LazyLoadImage)`
  margin-right: ${Spacings.SPACING_4B};
  user-drag: none;
  user-select: none;
  height: ${({ $isFloating }) =>
    $isFloating ? Spacings.SPACING_10B : Spacings.SPACING_12B};
  @media (max-width: 70.95em) {
    height: 40px;
  }
  @media (max-width: 47.95em) {
    height: 48px;
    margin: 0;
  }
  @media (max-width: 384px) {
    height: 40px;
    margin: 0;
  }
`;

const Phone = styled(LazyLoadImage)`
  height: ${({ $isFloating }) => ($isFloating ? '289px' : '404px')};
  margin: ${({ $isFloating }) =>
    $isFloating ? 'auto' : `${Spacings.SPACING_10B} 128px 0px 0px`};
  ${({ $isFloating }) => ($isFloating ? 'display: block;' : '')}
  user-drag: none;
  user-select: none;
  @media (max-width: 70.95em) {
    width: 240px;
    height: 254px;
  }
  @media (max-width: 47.95em) {
    width: 244px;
    height: 259px;
    margin: 0;
  }
`;

const ANDROID_LEARNER_APP =
  'https://play.google.com/store/apps/details?id=com.unacademyapp&referrer=utm_source%3DFooter%26utm_medium%3DWebApp%26utm_campaign%3DFooter%26';
const ANDROID_EDUCATOR_APP =
  'https://play.google.com/store/apps/details?id=com.unacademy&referrer=utm_source%3DFooter%26utm_medium%3DWebApp%26utm_campaign%3DFooter%26';
const IOS_LEARNER_APP =
  'https://apps.apple.com/in/app/unacademy-learning-app/id1342565069?referrer%3Dutm_source%253DFooter%2526utm_medium%253DWebApp%2526utm_campaign%253DFooter%2526';
const IOS_EDUCATOR_APP =
  'https://apps.apple.com/in/app/unacademy-educator-app/id1156375099%26referrer%3Dutm_source%253DFooter%2526utm_medium%253DWebApp%2526utm_campaign%253DFooter%2526';

const EduTitle = 'All in one place';
const LearnerTitle = 'Get the learning app';
const EduSubText =
  'Schedule your classes, engage with learners, see your performance across your courses';
const LearnSubText =
  'Download lessons and learn anytime, anywhere with the Unacademy app';

const Content = ({ className, isFloating, isEducator }) => {
  const theme = useContext(ThemeContext);
  return (
    <SectionWrapper className={className} $isFloating={isFloating}>
      <TextWrapper $isFloating={isFloating}>
        <Label color={theme.colors.fontGrey} $isFloating={isFloating}>
          {isEducator ? EduTitle : LearnerTitle}
        </Label>
        <SecLabel color={theme.colors.fontGrey} $isFloating={isFloating}>
          {isEducator ? EduSubText : LearnSubText}
        </SecLabel>
        <AppContainer $isFloating={isFloating}>
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={isEducator ? IOS_EDUCATOR_APP : IOS_LEARNER_APP}
          >
            <Img
              $isFloating={isFloating}
              src={imageHelper.getImage('app_store.png')}
              alt="appStore"
            />
          </a>
          <a
            href={isEducator ? ANDROID_EDUCATOR_APP : ANDROID_LEARNER_APP}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Img
              $isFloating={isFloating}
              src={imageHelper.getImage('play_store.png')}
              alt="playStore"
            />
          </a>
        </AppContainer>
      </TextWrapper>
      <PhoneWrapper $isFloating={isFloating}>
        <Phone
          $isFloating={isFloating}
          src={imageHelper.getImage(
            isEducator ? 'educatorApp.png' : 'phones.png'
          )}
          alt=""
        />
      </PhoneWrapper>
    </SectionWrapper>
  );
};

Content.propTypes = {
  className: PropTypes.string,
  isFloating: PropTypes.bool,
  isEducator: PropTypes.bool
};

Content.defaultProps = {
  className: '',
  isFloating: false,
  isEducator: false
};

const LearnOnTheGo = ({
  className,
  isFloating,
  modalState,
  onClose,
  isEducator
}) => {
  if (isFloating) {
    return (
      <CustomModal
        overlayTransparent
        modalState={modalState}
        onClose={onClose}
        bottomFrom="56px"
        bottomTo="76px"
        gridColumn="6/15"
      >
        <Content
          className={className}
          isFloating={isFloating}
          isEducator={isEducator}
        />
      </CustomModal>
    );
  }
  return (
    <Content
      className={className}
      isFloating={isFloating}
      isEducator={isEducator}
    />
  );
};

export default LearnOnTheGo;

LearnOnTheGo.propTypes = {
  className: PropTypes.string,
  isFloating: PropTypes.bool,
  modalState: PropTypes.bool,
  onClose: PropTypes.func,
  isEducator: PropTypes.bool
};

LearnOnTheGo.defaultProps = {
  className: '',
  isFloating: false,
  modalState: false,
  onClose: () => {},
  isEducator: false
};
